import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { getServices } from '@infrastructure/services';
import { Banner, Button, Card, Chip, Field, Modal, cssVars } from '../../design-system';
import { MONTHS_SHORT, formatDateRange } from '../../format';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import { loadFailed } from '../../loadError';
import { awaitsMyAnswer } from './replacementAnswer';
import shared from '../shared.module.css';
/*
 * La hoja de las solicitudes propias, no una copia.
 *
 * Es la misma lista y la misma clase de objeto —unas vacaciones con sus fechas
 * y su folio—, sólo que de otra persona. Rehacer aquí la tarjeta habría creado
 * dos diseños que empiezan iguales y se separan al primer retoque.
 */
import rows from './RequestsScreen.module.css';
import styles from './ReplacementInbox.module.css';

/**
 * «Te designaron como respaldo» — la mitad que faltaba de la conversación.
 *
 * El portal ya mandaba el correo a quien habías nombrado, pero esa persona no
 * tenía dónde contestar: la solicitud llegaba al líder diciendo «reemplazo:
 * Deyanira» como si Deyanira lo hubiera aceptado. Nombrar a alguien no es que
 * haya aceptado, y quien va a cargar con el trabajo merece que se lo pregunten.
 *
 * Vive con las solicitudes propias y sólo aparece cuando hay algo que
 * responder. Una sección permanentemente vacía enseña a ignorarla, y ésta es
 * justo la que no conviene que se ignore: mientras no se conteste, el líder no
 * puede aprobar.
 */

/** Sólo mientras la respuesta todavía cambia algo. */
const OPEN = ['Por revisar', 'En revisión', 'Visto bueno'] as const;

export const replacementInboxKey = (personId: string): readonly unknown[] => [
  'requests',
  'covering',
  personId,
];

export const ReplacementInbox = (): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [declining, setDeclining] = useState<VacationRequest | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const covering = useQuery({
    queryKey: replacementInboxKey(session.personId),
    queryFn: async () => {
      const result = await getServices().requests.list(
        { replacementPersonId: session.personId, statuses: [...OPEN] },
        { limit: 20 },
      );
      /*
       * Un fallo aquí se dice; no se devuelve como lista vacía.
       *
       * Devolverla es lo que costó dos rondas de depuración: a esta consulta le
       * faltaba un índice compuesto y fallaba entera, pero la sección se
       * escondía —igual que cuando no hay nada que contestar— y desde fuera
       * ambas cosas se veían idénticas. El respaldo decía «no me aparece» y la
       * pantalla no tenía forma de contradecirla. Un error mudo en una sección
       * que se oculta sola es un error que nadie va a encontrar.
       */
      if (!result.ok) throw loadFailed('No pudimos cargar tus reemplazos.', result.error);
      return result.value.items;
    },
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['requests'] });
    await queryClient.invalidateQueries({ queryKey: ['approvals'] });
    await queryClient.invalidateQueries({ queryKey: ['shell', 'badge'] });
  };

  /*
   * Al fallar, la sección aparece diciéndolo. Es la única forma de que «no me
   * aparece» y «no tengo nada» dejen de ser la misma pantalla.
   */
  if (covering.error !== null) {
    return (
      <Banner tone="danger">
        No pudimos cargar las solicitudes en las que eres respaldo. Vuelve a cargar la página;
        si sigue igual, avísale a quien administra el portal.
      </Banner>
    );
  }

  const pending = (covering.data ?? []).filter(awaitsMyAnswer);
  if (pending.length === 0) return null;

  const accept = async (request: VacationRequest): Promise<void> => {
    setBusyId(request.id);
    const result = await getServices().requests.respondToReplacement({
      requestId: request.id,
      answer: 'accept',
      reason: null,
    });
    setBusyId(null);

    if (!result.ok) {
      toast.bad('No se pudo registrar tu respuesta.');
      return;
    }
    toast.ok(`Aceptaste cubrir a ${request.personName}.`);
    await refresh();
  };

  return (
    <Card
      title="Te designaron como respaldo"
      note="Confirma si puedes cubrir. Hasta que respondas, su líder no puede aprobar."
    >
      <ul className={rows.list}>
        {pending.map((request) => (
          <li
            key={request.id}
            className={rows.card}
            /* Ámbar, no el color del estado: en una lista de solicitudes
               propias esto es lo ajeno, y el color es lo que lo separa antes de
               leer una palabra. */
            style={cssVars({ '--date-accent': 'var(--color-warning)' })}
          >
            <div className={rows.dateBox}>
              <span className={rows.dateMonth}>
                {MONTHS_SHORT[request.range.start.month - 1]}
              </span>
              <span className={rows.dateDay}>{request.range.start.day}</span>
            </div>

            <div className={rows.body}>
              <div className={rows.range}>{formatDateRange(request.range)}</div>
              <div className={rows.meta}>
                Vacaciones de {request.personName} · {request.folio} · {request.businessDays} días
                hábiles
              </div>
              {/* Lo que decide la respuesta: si puedes cubrirlo depende de qué
                  es «lo». */}
              {request.notes.length > 0 && <p className={styles.notes}>{request.notes}</p>}
            </div>

            <div className={rows.statusCell}>
              <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
                Espera tu respuesta
              </Chip>
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.accept}
                disabled={busyId !== null}
                onClick={() => {
                  void accept(request);
                }}
              >
                Aceptar reemplazo
              </button>
              <button
                type="button"
                className={styles.decline}
                disabled={busyId !== null}
                onClick={() => {
                  setDeclining(request);
                }}
              >
                No puedo
              </button>
            </div>
          </li>
        ))}
      </ul>

      {declining !== null && (
        <DeclineDialog
          request={declining}
          onClose={() => {
            setDeclining(null);
          }}
          onDone={async () => {
            setDeclining(null);
            await refresh();
          }}
        />
      )}
    </Card>
  );
};

/**
 * Decir que no, con el motivo.
 *
 * Obligatorio porque el solicitante tiene que elegir a otra persona y la razón
 * cambia a quién: «tengo entrega esa semana» y «no conozco ese módulo» llevan a
 * respaldos distintos. Un «no puedo» a secas lo deja eligiendo a ciegas.
 */
const DeclineDialog = ({
  request,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (reason.trim().length === 0) return;
    setBusy(true);

    const result = await getServices().requests.respondToReplacement({
      requestId: request.id,
      answer: 'decline',
      reason: reason.trim(),
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad('No se pudo registrar tu respuesta.');
      return;
    }
    toast.ok(`${request.personName} sabrá que no puedes cubrirlo.`);
    await onDone();
  };

  return (
    <Modal
      title={`No puedes cubrir a ${request.personName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.trim().length === 0}
            onClick={() => {
              void submit();
            }}
          >
            Enviar respuesta
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          {request.personName} verá tu motivo y podrá elegir otro respaldo sin volver a
          capturar la solicitud. Sus días del {formatDateRange(request.range)} siguen en pie.
        </p>

        <Field label="Por qué no puedes cubrirlo">
          {(id) => (
            <textarea
              id={id}
              className={styles.reasonText}
              rows={3}
              maxLength={500}
              placeholder="Ej. Tengo la entrega de la app esa misma semana."
              value={reason}
              onChange={(event) => {
                setReason(event.target.value.slice(0, 500));
              }}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
};
